import { SortOrder } from 'src/services/common.types';
import { SortConfig } from 'src/services/api.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';

/**
 * Sorting a streamed search's rows in the browser (JT1-1802). A streamed search holds at most
 * 1,000 rows, all already loaded, so a column header sorts them instantly rather than asking the
 * server to search again.
 *
 * The keys and their fields follow the backend's `LEAN_SORT_FIELDS`, so a column sorts the same way
 * whichever path served the list: `name` sorts on first name, exactly as the server does.
 */
const SORT_VALUES: Record<string, (row: IOrgCandidateListItem) => string | number | null | undefined> = {
  name: (row) => row.first_name,
  position_title: (row) => row.position_title,
  location: (row) => row.location,
  availability: (row) => row.availability_status,
  availability_status: (row) => row.availability_status,
  rating: (row) => row.rating,
  email: (row) => row.email,
  last_activity_at: (row) => (row.last_activity_at ? Date.parse(row.last_activity_at) : null),
};

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

/**
 * A sorted copy of `rows`. Blanks sort last in both directions, so an ascending name sort does not
 * open with a page of nameless rows. An unknown key leaves the order as it is.
 */
export function sortCandidateRows(rows: IOrgCandidateListItem[], sort: SortConfig): IOrgCandidateListItem[] {
  const valueOf = sort.sort_by ? SORT_VALUES[sort.sort_by] : undefined;
  if (!valueOf) return rows;

  const direction = sort.sort_order === SortOrder.ASC ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = valueOf(a);
    const right = valueOf(b);
    if (isBlank(left) && isBlank(right)) return 0;
    if (isBlank(left)) return 1;
    if (isBlank(right)) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;
    return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * direction;
  });
}
